import * as React from 'react';
import { TCartReview } from './CartReview.types';
import './CartReview.styled.scss';
import { IconButton, Button, Divider } from '@mui/material';
import { Delete, Remove, Add, ErrorOutline } from '@mui/icons-material';
import { useAppDispatch } from 'store/store';
import { getStockListByIdsAsyncThunk } from 'store/stock/stock.action';
import { TStockItem } from 'api/stock/stock.types';
import { getProductsListByIdsAsyncThunk } from 'store/products/products.action';
import { TProductBEItem } from 'api/products/products.types';
import {
  checkCartStockFulfillment,
  getOutdatedPricesProducts,
  getMissingStockItems,
  hasMissingStockItems,
} from 'components/templates/Cart/Cart.utils';
import { NOTIFICATION_TYPES } from 'store/notifications/notifications.types';
import { pushNotification } from 'store/notifications/notifications.slices';
import { updateItemPriceInLocalCartById } from 'store/localCart';
import { useLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.provider';

export const CartReview: TCartReview = ({
  items,
  onQuantityChange,
  onRemoveItem,
  onContinue,
}) => {
  const localize = useLocalize();
  const dispatch = useAppDispatch();
  const [stockVocabulary, setStockVocabulary] = React.useState<
    Record<TStockItem['id'], TStockItem>
  >({});

  const subtotal = React.useMemo(() => {
    return items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }, [items]);

  const stockItemsInCart = React.useMemo(() => {
    return items.reduce(
      (acc, cartItem) => {
        cartItem.stock?.forEach((stock) => {
          if (!stock.stockItem?.id) {
            return;
          }
          if (!acc[stock.stockItem.id]) {
            acc[stock.stockItem.id] = stock.quantity * cartItem.quantity;
          } else {
            acc[stock.stockItem.id] += stock.quantity * cartItem.quantity;
          }
        });
        return acc;
      },
      {} as Record<string, number>
    );
  }, [items]);

  const missingStockItems = React.useMemo(() => {
    return getMissingStockItems(stockItemsInCart, stockVocabulary);
  }, [stockItemsInCart, stockVocabulary]);

  const hasMissingStockItemsInCart = React.useMemo(() => {
    return Object.keys(missingStockItems).length > 0;
  }, [missingStockItems]);

  const handleQuantityChange = (itemId: string, delta: number) => {
    onQuantityChange(itemId, delta);
  };

  const handleRemove = (itemId: string) => {
    onRemoveItem(itemId);
  };

  const handleContinue = () => {
    dispatch(
      getProductsListByIdsAsyncThunk({
        queryParams: {
          idList: Array.from(new Set(items.map((item) => item.productId))),
        },
        onSuccess: (products) => {
          const checkProductsMap = new Map<string, TProductBEItem>(
            products.items?.map?.((checkProduct) => [
              checkProduct.id,
              checkProduct,
            ]) ?? []
          );

          const cartItemsToUpdate = getOutdatedPricesProducts(
            items,
            checkProductsMap
          );
          if (cartItemsToUpdate.length > 0) {
            dispatch(
              pushNotification({
                message: localize('cart.notification.outdated.prices', [
                  Array.from(
                    new Set(
                      cartItemsToUpdate.map(
                        (item) => localize(item.productName) as string
                      )
                    )
                  ).join(', '),
                ]) as string,
                type: NOTIFICATION_TYPES.WARNING,
              })
            );
            cartItemsToUpdate.forEach((item) => {
              dispatch(
                updateItemPriceInLocalCartById({
                  id: item.id,
                  price: item.price,
                })
              );
            });

            return;
          }

          dispatch(
            getStockListByIdsAsyncThunk({
              queryParams: {
                idList: Object.keys(stockItemsInCart),
              },
              onSuccess: (stockItems) => {
                const stockItemsVocabularyIncoming = stockItems.reduce<
                  Record<TStockItem['id'], TStockItem>
                >((acc, item) => {
                  acc[item.id] = item;
                  return acc;
                }, {});
                setStockVocabulary(stockItemsVocabularyIncoming);

                const isStockFulfillment = checkCartStockFulfillment(
                  stockItemsInCart,
                  stockItemsVocabularyIncoming
                );
                if (isStockFulfillment) {
                  onContinue(stockItemsInCart);
                  setStockVocabulary({});
                }
              },
            })
          );
        },
      })
    );
  };

  if (items.length === 0) {
    return (
      <div className="cart-review">
        <div className="cart-review__empty">
          <h6>{localize('cart.review.empty.title')}</h6>
          <p className="body2">{localize('cart.review.empty.hint')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-review">
      <div className="cart-review__header">
        <h6 className="cart-review__title">{localize('cart.review.title')}</h6>
      </div>
      {hasMissingStockItemsInCart ? (
        <div className="cart-review__banner cart-review__banner--error">
          <p>{localize('cart.review.stock.error')}</p>
          <ol className="cart-review__stock-errors-list">
            {Object.entries(missingStockItems).map(([stockItemId]) => (
              <li key={stockItemId}>
                {localize('cart.stock.row', [
                  [
                    stockVocabulary[stockItemId]?.name,
                    stockVocabulary[stockItemId]?.variation,
                  ]
                    .filter(Boolean)
                    .join(', '),
                  String(stockVocabulary[stockItemId]?.quantity ?? 0),
                ])}
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      <div className="cart-review__items">
        {items.map((item) => (
          <div key={item.id} className="cart-review__item">
            <div className="cart-review__item-image">
              <img
                src={item.productImage}
                alt={localize(item.productName) as string}
              />
            </div>
            <div className="cart-review__item-details">
              <div className="cart-review__item-title-container">
                <h6 className="cart-review__item-title">
                  {localize(item.productName) as string}
                </h6>
                {hasMissingStockItems(item.stock, missingStockItems) ? (
                  <ErrorOutline color="error" />
                ) : null}
              </div>
              {Object.keys(item.customOptions).length > 0 && (
                <p className="cart-review__item-description body2">
                  {Object.entries(item.customOptions)
                    .map(
                      ([key, value]) => `${localize(key) as string}: ${value}`
                    )
                    .join(' / ')}
                </p>
              )}
              {item.stock.length > 0 && (
                <p className="cart-review__item-description body2">
                  {item.stock
                    .map(
                      (stockEntry) =>
                        `${stockEntry.stockItem.name}${
                          stockEntry.stockItem.variation
                            ? `, ${stockEntry.stockItem.variation}`
                            : ''
                        }`
                    )
                    .join(' / ')}
                </p>
              )}
              <div className="cart-review__item-footer">
                <div className="cart-review__item-controls">
                  <div className="cart-review__quantity-control">
                    <IconButton
                      size="small"
                      onClick={() => handleQuantityChange(item.id, -1)}
                      className="cart-review__quantity-button"
                    >
                      <Remove fontSize="small" />
                    </IconButton>
                    <span className="cart-review__quantity-value">
                      {item.quantity}
                    </span>
                    <IconButton
                      size="small"
                      onClick={() => handleQuantityChange(item.id, 1)}
                      className="cart-review__quantity-button"
                    >
                      <Add fontSize="small" />
                    </IconButton>
                  </div>
                  <IconButton
                    onClick={() => handleRemove(item.id)}
                    className="cart-review__delete-button"
                    aria-label={localize('cart.aria.remove.item') as string}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </div>
                <div className="cart-review__item-price">
                  <h6>{(item.price * item.quantity).toFixed(2)} zł</h6>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="cart-review__bottom-block">
        <Divider className="cart-review__bottom-block__divider" />

        <div className="cart-review__bottom-block__summary">
          <div className="cart-review__bottom-block__summary-row">
            <p>{localize('cart.review.subtotal')}</p>
            <h6>{subtotal.toFixed(2)} zł</h6>
          </div>
          <div className="cart-review__bottom-block__summary-row">
            <p className="body2">{localize('cart.review.shipping.hint')}</p>
          </div>
        </div>

        <div className="cart-review__bottom-block__actions">
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleContinue}
            className="cart-review__bottom-block__continue-button"
          >
            {localize('cart.review.continue.shipping')}
          </Button>
        </div>
      </div>
    </div>
  );
};
